import type {
  AdminLicenseComplianceDto,
  UserComplianceSummaryDto,
} from '../../services/complianceReportService';
import { getAtRisk } from './atRisk';
import { getUrgency } from './urgency';

/**
 * Client-side status model for the admin Team Reporting page.
 * A license is "set up" once its requirement row was modified or confirmed;
 * only set-up licenses have targets to measure against.
 */
export function isLicenseSetUp(license: AdminLicenseComplianceDto): boolean {
  return license.status === 'modified' || license.status === 'confirmed';
}

/**
 * Derived (home-state reciprocity) rows carry no local hours: their compliance
 * mirrors the member's primary license — listed alongside in the same report —
 * plus their own ethics, which the backend populates only for ethics-carve-out
 * states: hours fields for hours-tracked ethics, manual subject requirement
 * rows for attested board-approved courses (e.g. FL). Pass `memberLicenses`
 * wherever it's available so derived rows resolve; without it they
 * conservatively read as non-compliant.
 */
export function isLicenseCompliant(
  license: AdminLicenseComplianceDto,
  memberLicenses?: AdminLicenseComplianceDto[]
): boolean {
  if (!isLicenseSetUp(license)) return false;
  if (license.complianceMode === 'home_state_reciprocity') {
    const primary = memberLicenses?.find((l) => l.isPrimary && l.licenseId !== license.licenseId);
    return (
      primary != null &&
      isLicenseCompliant(primary) &&
      (license.ethicsEarnedHours ?? 0) >= (license.ethicsTargetHours ?? 0) &&
      !hasUnfulfilledManualRequirement(license)
    );
  }
  // Standard rows: the backend computes the verdict (hours + ethics + every
  // subject/manual requirement) so admin and member views can never disagree.
  return license.isCompliant ?? false;
}

/** True when any manual (attested) requirement is unfulfilled within the current period. */
export function hasUnfulfilledManualRequirement(license: AdminLicenseComplianceDto): boolean {
  return license.subjectRequirements.some((r) => r.targetKind === 'manual' && !r.isMet);
}

export type LicenseStanding = 'not_set_up' | 'in_progress' | 'concluded' | 'attention';

export function getLicenseStanding(
  license: AdminLicenseComplianceDto,
  now: Date = new Date(),
  memberLicenses?: AdminLicenseComplianceDto[]
): LicenseStanding {
  if (!isLicenseSetUp(license)) return 'not_set_up';
  const compliant = isLicenseCompliant(license, memberLicenses);
  if (compliant) return 'concluded';
  if (license.periodEnd && getUrgency(license.periodEnd, compliant, now).attentionRequired) {
    return 'attention';
  }
  return 'in_progress';
}

/**
 * Predictive at-risk for the admin report — only meaningful for set-up licenses
 * still in progress; "Attention Required" (reactive, ≤30 days) supersedes it,
 * so attention/concluded/not_set_up standings never show At Risk.
 */
export function isAdminLicenseAtRisk(
  license: AdminLicenseComplianceDto,
  now: Date = new Date(),
  memberLicenses?: AdminLicenseComplianceDto[]
): boolean {
  if (!license.periodStart || !license.periodEnd || license.targetHours == null) return false;
  if (getLicenseStanding(license, now, memberLicenses) !== 'in_progress') return false;
  return getAtRisk(
    {
      periodStart: license.periodStart,
      periodEnd: license.periodEnd,
      requiredTotalHours: license.targetHours,
      earnedTotalHours: license.earnedHours ?? 0,
      isCompliant: isLicenseCompliant(license, memberLicenses),
      hasUnfulfilledManualRequirement: hasUnfulfilledManualRequirement(license),
    },
    now
  ).atRisk;
}

/** Worst-first severity for the member-level confirm-status rollup. */
const CONFIRM_SEVERITY: AdminLicenseComplianceDto['status'][] = [
  'not_set_up',
  'unconfirmed',
  'modified',
  'confirmed',
];

/**
 * Worst-status-wins provenance rollup for a member's licenses — an admin only
 * needs one glance to know whether every license's requirements are confirmed.
 * Returns null when the member has no licenses on file.
 */
export function getMemberConfirmStatus(
  member: UserComplianceSummaryDto
): AdminLicenseComplianceDto['status'] | null {
  const licenses = member.licenses ?? [];
  if (licenses.length === 0) return null;
  for (const status of CONFIRM_SEVERITY) {
    if (licenses.some((l) => l.status === status)) return status;
  }
  return 'confirmed';
}

export interface MemberStanding {
  /** Primary bucket: concluded when all set-up licenses are compliant (and at least one is set up). */
  status: 'in_progress' | 'concluded';
  /** Overlay: true when any license is within the attention window and non-compliant. */
  attentionRequired: boolean;
  /** Overlay: true when any license is projected to fall short at its current pace. */
  atRisk: boolean;
}

export function getMemberStanding(
  member: UserComplianceSummaryDto,
  now: Date = new Date()
): MemberStanding {
  const licenses = member.licenses ?? [];
  const setUp = licenses.filter(isLicenseSetUp);
  const attentionRequired = licenses.some((l) => getLicenseStanding(l, now, licenses) === 'attention');
  const atRisk = licenses.some((l) => isAdminLicenseAtRisk(l, now, licenses));
  const concluded = setUp.length > 0 && setUp.every((l) => isLicenseCompliant(l, licenses));
  return { status: concluded ? 'concluded' : 'in_progress', attentionRequired, atRisk };
}
